import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Table, Badge, Form, Row, Col, Spinner, Button, InputGroup, Pagination } from 'react-bootstrap';
import {
    LuSearch,
    LuDownload,
    LuArrowUpRight,
    LuCircleDot,
    LuCreditCard,
    LuRefreshCw,
    LuChevronLeft,
    LuChevronRight
} from 'react-icons/lu';
import { fetchBusinessTransactions, syncStripeStatus, syncPendingTransactions } from '../../../redux/reducers/transactionSlice';
import { getCustomersByBusiness } from '../../../redux/reducers/customerSlice';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../../utils/currency';

const TransactionList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const activeBusinessId = useSelector((state) => state.business.activeBusinessId);
    const currency = useSelector((state) => state.business?.currencyContext?.currency || 'CAD');
    const { transactions, loading, pagination } = useSelector((state) => state.transaction);

    const [timeFilter, setTimeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [customDate, setCustomDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [isSyncing, setIsSyncing] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);

    const loadData = useCallback((page = 1) => {
        if (activeBusinessId) {
            const filters = {
                page,
                limit,
                status: statusFilter === 'all' ? '' : statusFilter,
                timeFilter: timeFilter === 'all' ? '' : timeFilter,
                customDate: timeFilter === 'specific' ? customDate : ''
            };
            dispatch(fetchBusinessTransactions({ businessId: activeBusinessId, filters }));
            dispatch(getCustomersByBusiness(activeBusinessId));
        }
    }, [dispatch, activeBusinessId, statusFilter, timeFilter, customDate, limit]);

    useEffect(() => {
        setCurrentPage(1);
        loadData(1);
    }, [statusFilter, timeFilter, customDate, limit, activeBusinessId]);

    useEffect(() => {
        loadData(currentPage);
    }, [currentPage]);

    const handleSync = async () => {
        if (!activeBusinessId) return;
        setIsSyncing(true);
        try {
            await dispatch(syncStripeStatus(activeBusinessId)).unwrap();
            await dispatch(syncPendingTransactions()).unwrap();
            toast.success('Sync complete. Latest transactions retrieved.');
            loadData(currentPage);
        } catch (err) {
            toast.error(err || 'Failed to sync with Stripe');
        } finally {
            setIsSyncing(false);
        }
    };

    const displayTransactions = (transactions || []).filter(item => {
        const name = item.customerId?.name || item.customerName || '';
        const email = item.customerId?.email || item.customerEmail || '';
        return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            email.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const getStatusBadge = (status) => {
        switch (status) {
            case 'captured': return <Badge bg="success-subtle" className="text-success rounded-pill px-3">Paid</Badge>;
            case 'pending':  return <Badge bg="warning-subtle" className="text-warning rounded-pill px-3">Pending</Badge>;
            case 'authorized': return <Badge bg="info-subtle" className="text-info rounded-pill px-3">Authorized</Badge>;
            case 'refunded': return <Badge bg="danger-subtle" className="text-danger rounded-pill px-3">Refunded</Badge>;
            case 'voided':   return <Badge bg="secondary-subtle" className="text-secondary rounded-pill px-3">Cancelled</Badge>;
            default:         return <Badge bg="primary-subtle" className="text-primary rounded-pill px-3">{status}</Badge>;
        }
    };

    return (
        <div className="mt-4">
            {/* Filter Bar */}
            <Card className="border-0 shadow-sm rounded-4 p-3 mb-4">
                <Row className="g-3 align-items-end">
                    <Col lg={3} md={6}>
                        <Form.Label className="small fw-bold text-muted mb-1">Search</Form.Label>
                        <InputGroup className="bg-light rounded-pill overflow-hidden border-0">
                            <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted">
                                <LuSearch size={16} />
                            </InputGroup.Text>
                            <Form.Control
                                placeholder="Name or email..."
                                className="bg-transparent border-0 shadow-none py-2 small"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </Col>

                    <Col lg={2} md={6}>
                        <Form.Label className="small fw-bold text-muted mb-1">Time Period</Form.Label>
                        <Form.Select
                            className="bg-light border-0 rounded-pill shadow-none small fw-bold py-2"
                            value={timeFilter}
                            onChange={(e) => setTimeFilter(e.target.value)}
                        >
                            <option value="all">All Time</option>
                            <option value="today">Today</option>
                            <option value="thisweek">This Week</option>
                            <option value="thismonth">This Month</option>
                            <option value="specific">Specific Date</option>
                        </Form.Select>
                    </Col>

                    {timeFilter === 'specific' && (
                        <Col lg={2} md={6}>
                            <Form.Label className="small fw-bold text-muted mb-1">Pick Date</Form.Label>
                            <Form.Control
                                type="date"
                                className="bg-light border-0 rounded-pill shadow-none py-2 small"
                                value={customDate}
                                onChange={(e) => setCustomDate(e.target.value)}
                            />
                        </Col>
                    )}

                    <Col lg={2} md={6}>
                        <Form.Label className="small fw-bold text-muted mb-1">Status</Form.Label>
                        <Form.Select
                            className="bg-light border-0 rounded-pill shadow-none small fw-bold py-2"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="captured">Paid</option>
                            <option value="pending">Pending</option>
                            <option value="authorized">Authorized</option>
                            <option value="refunded">Refunded</option>
                            <option value="voided">Cancelled</option>
                        </Form.Select>
                    </Col>

                    <Col lg="auto" className="ms-auto d-flex gap-2">
                        <Button
                            variant="light"
                            className="rounded-pill px-3 shadow-sm d-flex align-items-center gap-2 border small fw-bold py-2"
                            onClick={handleSync}
                            disabled={isSyncing || loading}
                        >
                            <LuRefreshCw size={16} className={isSyncing ? 'spin-animation' : ''} />
                            <span className="d-none d-md-inline">{isSyncing ? 'Syncing...' : 'Sync'}</span>
                        </Button>
                        <Button
                            variant="outline-secondary"
                            className="rounded-pill px-3 d-inline-flex align-items-center gap-2 small fw-bold py-2"
                        >
                            <LuDownload size={16} /> Export CSV
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Table */}
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <Card.Body className="p-0">
                    {loading && !isSyncing ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="text-muted small mt-2">Fetching transactions...</p>
                        </div>
                    ) : (
                        <Table responsive hover className="align-middle mb-0">
                            <thead className="bg-light text-muted small text-uppercase">
                                <tr>
                                    <th className="ps-4 py-3 fw-semibold">Transaction</th>
                                    <th className="fw-semibold">Customer</th>
                                    <th className="fw-semibold">Reference</th>
                                    <th className="fw-semibold">Amount</th>
                                    <th className="fw-semibold">Status</th>
                                    <th className="pe-4 text-end fw-semibold">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayTransactions.length > 0 ? (
                                    displayTransactions.map((item) => (
                                        <tr key={item._id}>
                                            <td className="ps-4 py-3">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="p-2 bg-soft-brand rounded-3 text-brand">
                                                        <LuCreditCard size={20} />
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold small">#{item._id.slice(-6).toUpperCase()}</div>
                                                        <div className="text-muted" style={{ fontSize: '11px' }}>
                                                            {moment(item.createdAt).format('MMM DD, YYYY')}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="small fw-semibold">
                                                    {item.customerId?.name || item.customerName || 'Manual Payment'}
                                                </div>
                                                <div className="text-muted" style={{ fontSize: '11px' }}>
                                                    {item.customerId?.email || item.customerEmail}
                                                </div>
                                            </td>
                                            <td>
                                                <Badge bg="light" className="text-dark border rounded-pill fw-normal px-2 py-1 small">
                                                    {item.invoiceId
                                                        ? `Invoice #${item.invoiceId.toString().slice(-6).toUpperCase()}`
                                                        : (item.description || 'Direct Link')}
                                                </Badge>
                                            </td>
                                            <td>
                                                <div className="fw-bold text-brand">
                                                    {formatCurrency(item.financials?.totalAmount || 0, item.financials?.currency || currency)}
                                                </div>
                                            </td>
                                            <td>{getStatusBadge(item.status)}</td>
                                            <td className="pe-4 text-end">
                                                <Button
                                                    variant="light"
                                                    size="sm"
                                                    className="rounded-circle p-2"
                                                    title="View details"
                                                    onClick={() => navigate(`/transaction-detail/${item._id}`)}
                                                >
                                                    <LuArrowUpRight size={18} className="text-muted" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5 text-muted">
                                            <LuCircleDot size={40} className="mb-2 opacity-25" />
                                            <p className="mb-0 small">No transactions found for the selected filters.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>

                {/* Pagination Footer */}
                {pagination && (
                    <Card.Footer className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <div className="d-flex align-items-center gap-2">
                                <small className="text-muted fw-medium">Rows per page:</small>
                                <Form.Select
                                    size="sm"
                                    className="border-0 bg-light rounded-3 fw-bold py-1 px-2"
                                    style={{ width: '70px', fontSize: '0.8rem' }}
                                    value={limit}
                                    onChange={(e) => { setLimit(Number(e.target.value)); setCurrentPage(1); }}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={15}>15</option>
                                    <option value={20}>20</option>
                                </Form.Select>
                            </div>
                            <small className="text-muted fw-medium border-start ps-3">
                                Page <strong>{pagination.page}</strong> of <strong>{pagination.pages}</strong>
                                &nbsp;({pagination.total} total)
                            </small>
                        </div>

                        <Pagination className="mb-0 gap-1" style={{ '--bs-pagination-active-bg': '#28B0C4', '--bs-pagination-active-border-color': '#28B0C4' }}>
                            <Pagination.Prev
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                                className="border-0 shadow-none"
                            >
                                <LuChevronLeft />
                            </Pagination.Prev>
                            {[...Array(pagination.pages)].map((_, idx) => (
                                <Pagination.Item
                                    key={idx + 1}
                                    active={idx + 1 === currentPage}
                                    onClick={() => setCurrentPage(idx + 1)}
                                >
                                    {idx + 1}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                disabled={currentPage === pagination.pages}
                                onClick={() => setCurrentPage(p => p + 1)}
                                className="border-0 shadow-none"
                            >
                                <LuChevronRight />
                            </Pagination.Next>
                        </Pagination>
                    </Card.Footer>
                )}
            </Card>

            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .spin-animation { animation: spin 1s linear infinite; }
            `}</style>
        </div>
    );
};

export default TransactionList;